import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import { api, type Organization, type OrgMembershipRow } from "@/lib/api";
import { qk } from "@/lib/queryKeys";

export function useOrgs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.orgs,
    queryFn: () => api.orgs.list(),
    enabled: options?.enabled ?? true,
  });
}

export function useSuspenseOrgMembers(orgId: string) {
  return useSuspenseQuery({
    queryKey: qk.orgMembers(orgId),
    queryFn: () => api.orgs.members(orgId),
  });
}

export function useOrgCreateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug: string }) => api.orgs.create(body),
    onSuccess: (org) => {
      qc.setQueryData(qk.org(org.id), org);
      // Seed the list cache so the new org is immediately present (selectable in
      // the switcher, and not treated as a stale selection by OrgContext) before
      // the invalidated list query refetches.
      qc.setQueryData<Organization[]>(qk.orgs, (prev) =>
        prev?.some((o) => o.id === org.id) ? prev : [...(prev ?? []), org]
      );
      void qc.invalidateQueries({ queryKey: qk.orgs });
    },
  });
}

export function useOrgUpdateMutation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; slug?: string; logo_url?: string | null }) => api.orgs.update(orgId, body),
    onSuccess: (org) => {
      qc.setQueryData<Organization>(qk.org(orgId), org);
      void qc.invalidateQueries({ queryKey: qk.orgs });
    },
  });
}

export function useOrgInviteMutation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role?: "member" | "admin" | "viewer" }) => api.orgs.invite(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) });
    },
  });
}

export function useOrgMemberUpdateMutation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { membershipId: string; role?: OrgMembershipRow["role"]; status?: string }) =>
      api.orgs.updateMember(orgId, vars.membershipId, { role: vars.role, status: vars.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) });
      void qc.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
}

export function useOrgMemberRemoveMutation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => api.orgs.removeMember(orgId, membershipId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) });
      void qc.invalidateQueries({ queryKey: qk.org(orgId) });
    },
  });
}
